import React from "react";
import FormInput from "@ats/src/components/forms/FormInput";
import FormTextarea from "@ats/src/components/forms/FormTextarea";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormUploader from "@ats/src/components/forms/FormUploader";

import {
  MEGABYTE_IN_BYTES,
  isAllowedFileSize,
  isAllowedFileType,
} from "@ats/src/lib/utils/fileUploads";
import { convertArrayToSelectOptions } from "@shared/lib/utils";

export default function QuestionResponse(props: {
  question: any;
  questionResponse: any;
  errors: any;
  index: number;
  handleQuestionResponseChange: (index, values, errors) => void;
  clearFixedErrors: (fieldName, errors) => any;
}) {
  const { question, questionResponse, errors } = props;
  let customQuestionsErrors =
    errors && errors.customQuestionsErrors ? errors.customQuestionsErrors : null;
  const { id, questionText, placeholderText, requirementSetting, options } = question;
  const name = `${id}`;
  const questionResponseBody = questionResponse ? questionResponse.body : "";
  const questionSelectedOptions = questionResponse ? questionResponse.responseArray : "";
  const fileToUpload = questionResponse ? questionResponse.customFile : undefined;
  const label = questionText;
  const isRequired = requirementSetting === "required";
  const selectOptions = convertArrayToSelectOptions(options.data);
  const maxFileSize = MEGABYTE_IN_BYTES * 10;

  const handleQuestionResponseChange = (name, value) => {
    const { question, index, errors } = props;
    const response = { questionId: question.id, body: value };
    props.handleQuestionResponseChange(index, response, errors);
  };

  const handleSelectChange = (name, value) => {
    const { question, index, errors } = props;
    window.logger("%c[QuestionResponse] handleSelectChange", "color: #1976D2", { name, value });
    props.handleQuestionResponseChange(index, { questionId: question.id, body: value }, errors);
  };

  const handleMultiSelectChange = (name, value) => {
    const { question, index, errors } = props;
    window.logger("%c[QuestionResponse] handleSelectChange", "color: #1976D2", { name, value });
    const selections = value.map((item) => item.value);
    const response = { questionId: question.id, responseArray: selections };
    props.handleQuestionResponseChange(index, response, errors);
  };

  /* Handle File Input & Error
  --===================================================-- */
  const handleFileInputChange = (value) => {
    const { question, index } = props;
    window.logger("%c[QuestionResponse] handleFileInputChange", "color: #1976D2", { name, value });
    const errors = props.clearFixedErrors("file", props.errors);
    const response = { questionId: question.id, customFile: value.fileToUpload };
    props.handleQuestionResponseChange(index, response, errors);
  };

  const onInvalidFile = (file, expectedFileType) => {
    const { question, index } = props;
    window.logger("%c[QuestionResponse] onInvalidFile", "color: #1976D2", {
      file,
      expectedFileType,
    });
    const errors = file ? invalidFileError(file) : props.errors;
    const response = { questionId: question.id, customFile: undefined };
    props.handleQuestionResponseChange(index, response, errors);
  };

  const invalidFileError = (file: File) => {
    let fileError: { file: string[] };
    if (!isAllowedFileType("combined", file.type)) {
      fileError = {
        file: [window.APP_TRANSLATIONS.custom_question_file_type_error],
      };
    } else if (!isAllowedFileSize(maxFileSize, file.size)) {
      fileError = {
        file: [window.APP_TRANSLATIONS.custom_question_file_size_error],
      };
    }
    return fileError;
  };

  /* Return Question Component by Kind
  --===================================================-- */
  if (question.kind === "text_short") {
    return (
      <FormInput
        onChange={handleQuestionResponseChange}
        label={label}
        isRequired={isRequired}
        requiredLabel={window.APP_TRANSLATIONS.required}
        name={name}
        placeholder={placeholderText}
        value={questionResponseBody}
        errors={customQuestionsErrors}
      />
    );
  } else if (question.kind === "text_long") {
    return (
      <FormTextarea
        onChange={handleQuestionResponseChange}
        label={label}
        isRequired={isRequired}
        requiredLabel={window.APP_TRANSLATIONS.required}
        name={name}
        placeholder={placeholderText}
        value={questionResponseBody}
        errors={customQuestionsErrors}
        rows={8}
      />
    );
  } else if (question.kind === "single_select") {
    return (
      <FormSelect
        name={name}
        label={label}
        description=""
        value={questionResponseBody}
        options={selectOptions}
        isRequired={isRequired}
        requiredLabel={window.APP_TRANSLATIONS.required}
        placeholder={window.APP_TRANSLATIONS.select_placeholder}
        isClearable={!isRequired}
        onChange={handleSelectChange}
        errors={customQuestionsErrors}
      />
    );
  } else if (question.kind === "multi_select") {
    return (
      <FormSelect
        name={name}
        label={label}
        description={window.APP_TRANSLATIONS.multi_select_description}
        value={questionSelectedOptions}
        options={selectOptions}
        isRequired={isRequired}
        requiredLabel={window.APP_TRANSLATIONS.required}
        isClearable={!isRequired}
        onChange={handleMultiSelectChange}
        errors={customQuestionsErrors}
        isMulti={true}
      />
    );
  } else if (question.kind === "file_upload") {
    return (
      <span key={name}>
        <FormUploader
          name={name}
          label={label}
          onChange={handleFileInputChange}
          htmlFor={name} //necessary to differentiate uploads
          placeholder={window.APP_TRANSLATIONS.attach_file_placeholder}
          expectedFileType="combined"
          file={fileToUpload}
          allowAutomaticUploading={false}
          fileIsAlreadyUploaded={false}
          maxFileSize={maxFileSize}
          onInvalidFile={onInvalidFile}
          onCompleteDirectUpload={() => undefined}
          loading={false}
          errors={customQuestionsErrors}
          isRequired={isRequired}
          requiredLabel={window.APP_TRANSLATIONS.required}
        />
      </span>
    );
  } else {
    return null;
  }
}
